import { Controller } from "@hotwired/stimulus"

// A custom-field group of the timesheet entry forms (configurable_time_sheets/_custom_field_group).
// When the group is collapsible it keeps the "2 erfasst" count on its header in step with
// the inputs, and opens the group when one of its hidden inputs fails browser validation
// (the browser cannot point at a closed <details>).
//
// A field counts once, whatever number of inputs it renders. Fields the group's conditions
// currently hide (dynamic-form) do not count. dynamic-form applies conditions after our
// input/change handler has run, and a source outside the group fires no event here at
// all, so a field's visibility flip is watched on its wrapper's class instead.
const SKIPPED_TYPES = ["hidden", "button", "submit", "reset"]
const FIELD_SELECTOR = '[data-dynamic-form-target="field"]'

export default class extends Controller {
  static targets = ["details", "count"]
  static values = {
    filledOne: String,
    filledOther: String,
    none: String,
  }

  connect() {
    this.refresh()
    if (!this.hasCountTarget) return

    this.observer = new MutationObserver((records) => {
      if (records.some((record) => record.target.matches?.(FIELD_SELECTOR))) this.refresh()
    })
    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  refresh() {
    if (!this.hasCountTarget) return

    const filled = this.fields().filter((field) => this.fieldFilled(field)).length
    const badge = this.countTarget
    badge.classList.toggle("bg-green-100", filled > 0)
    badge.classList.toggle("text-green-800", filled > 0)
    badge.classList.toggle("bg-gray-100", filled === 0)
    badge.classList.toggle("text-gray-600", filled === 0)
    badge.textContent = this.label(filled)
  }

  reveal() {
    if (this.hasDetailsTarget) this.detailsTarget.open = true
  }

  fields() {
    return Array.from(this.element.querySelectorAll(FIELD_SELECTOR)).filter(
      (field) => !field.classList.contains("hidden"),
    )
  }

  fieldFilled(field) {
    return Array.from(field.querySelectorAll("input, textarea, select")).some((input) => {
      if (input.disabled || SKIPPED_TYPES.includes(input.type)) return false
      if (input.type === "checkbox" || input.type === "radio") return input.checked
      if (input.type === "file") return input.files?.length > 0
      if (input.multiple && input.tagName === "SELECT") return input.selectedOptions.length > 0
      return input.value.trim() !== ""
    })
  }

  label(filled) {
    if (filled === 0) return this.noneValue
    if (filled === 1) return this.filledOneValue
    return this.filledOtherValue.replace("%{count}", filled)
  }
}
